function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef, useState } from 'react';
import { arrayOf, bool, func, instanceOf, object, oneOfType, string } from 'prop-types';
import clsx from 'clsx';
import { DesktopDatePicker as DatePicker } from '@mui/x-date-pickers-pro';
import { format as fnFormat, parse as fnParse } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
import SovosTextField from '../sovos-text-field';

// convert date object to string
const formatDate = (date, format) => {
  if (typeof date === 'string') return date;
  try {
    return fnFormat(date, format);
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

// convert date string to date object
const parseDate = (date, format) => {
  if (date !== null && date !== void 0 && date.getTime && date.getTime()) return date;
  const dateObj = fnParse(date, format, new Date());
  return dateObj.toString() === 'Invalid Date' ? undefined : dateObj;
};
const SovosDatePicker = /*#__PURE__*/forwardRef((_ref, ref) => {
  var _TextFieldProps$varia;
  let {
    classes,
    className,
    'data-testid': dataTestId,
    format: formatProps,
    invalidDateMessage,
    maxDate,
    maxDateMessage,
    minDate,
    minDateMessage,
    onChange,
    sx,
    TextFieldProps,
    value = null,
    ...datePickerProps
  } = _ref;
  const {
    spacing
  } = useTheme();
  const {
    t
  } = useMosaicTranslation();
  const [error, setError] = useState(false);
  const [showError, setShowError] = useState(false);
  const format = formatProps || t('datePicker.format');
  if (value === undefined && !error) {
    setError('invalidDate');
    setShowError(true);
  }
  if (value === null && showError) {
    setShowError(false);
  }
  const getHelperText = err => {
    var _TextFieldProps$helpe;
    switch (err) {
      case 'invalidDate':
        return invalidDateMessage !== null && invalidDateMessage !== void 0 ? invalidDateMessage : t('datePicker.invalidDateMessage');
      case 'minDate':
        return minDateMessage !== null && minDateMessage !== void 0 ? minDateMessage : t('datePicker.minDateMessage');
      case 'maxDate':
        return maxDateMessage !== null && maxDateMessage !== void 0 ? maxDateMessage : t('datePicker.maxDateMessage');
      default:
        return (_TextFieldProps$helpe = TextFieldProps === null || TextFieldProps === void 0 ? void 0 : TextFieldProps.helperText) !== null && _TextFieldProps$helpe !== void 0 ? _TextFieldProps$helpe : null;
    }
  };
  const handleChange = (newValue, _ref2) => {
    let {
      validationError
    } = _ref2;
    if (!newValue) {
      onChange(null);
      return;
    }
    if (validationError) {
      setError(validationError);
      setShowError(true);
    } else {
      setError(validationError);
      setShowError(false);
    }
    const dateValue = formatDate(newValue, format);
    if (dateValue) {
      onChange(dateValue);
    }
  };
  const handleError = newError => {
    if (newError === 'invalidDate') {
      setShowError(true);
    } else if (newError !== error) {
      setShowError(false);
    }
    setError(newError);
  };
  return /*#__PURE__*/React.createElement(DatePicker, _extends({}, datePickerProps, {
    format: format,
    maxDate: parseDate(maxDate, format),
    minDate: parseDate(minDate, format),
    onChange: handleChange,
    onError: handleError,
    showTodayButton: true,
    slotProps: {
      textField: {
        ...TextFieldProps,
        classes: {
          filledInputRoot: classes === null || classes === void 0 ? void 0 : classes.input
        },
        className: clsx('sovosDatePicker', className, TextFieldProps === null || TextFieldProps === void 0 ? void 0 : TextFieldProps.className),
        'data-testid': dataTestId,
        error: !!error,
        helperText: getHelperText(error),
        inputProps: {
          sx: {
            paddingInlineEnd: spacing(3)
          },
          ...(TextFieldProps === null || TextFieldProps === void 0 ? void 0 : TextFieldProps.inputProps)
        },
        ref,
        sx,
        variant: (_TextFieldProps$varia = TextFieldProps === null || TextFieldProps === void 0 ? void 0 : TextFieldProps.variant) !== null && _TextFieldProps$varia !== void 0 ? _TextFieldProps$varia : 'filled'
      }
    },
    slots: {
      textField: SovosTextField
    },
    value: value === undefined ? 'Invalid Date' : parseDate(value, format) || value || null
  }));
});
SovosDatePicker.propTypes = {
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * Format used to parse the date, must be a valid [unicode date
   * string](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table)
   */
  format: string,
  /**
   * Message to show when value is invalid
   */
  invalidDateMessage: string,
  /**
   * Callback fired when input value is changed and valid, and when the
   * input is blurred. Returns null if empty, the value if valid, or
   * undefined if invalid. `function(value: string | null | undefined) => void`
   */
  onChange: func.isRequired,
  /**
   * Maximum date that can be selected. If a `string`, expected to match the `format`
   */
  maxDate: oneOfType([instanceOf(Date), string]),
  /**
   * Message to show when value is below `minDate`
   */
  maxDateMessage: string,
  /**
   * Minimum date that can be selected. If a `string`, expected to match the `format`
   */
  minDate: oneOfType([instanceOf(Date), string]),
  /**
   * Message to show when value is below `minDate`
   */
  minDateMessage: string,
  /**
   * The system prop that allows defining system overrides as well as
   * additional CSS styles.
   */
  sx: oneOfType([arrayOf(oneOfType([func, object, bool])), func, object]),
  /**
   * Props passed to the SovosTextField
   */
  TextFieldProps: object,
  /**
   * Value of the input. `Date` object or `string`. If a `string`, it
   * should match `format`
   */
  value: oneOfType([instanceOf(Date), string])
};
SovosDatePicker.baseComponent = {
  name: 'DatePicker',
  link: 'date-picker/'
};
export default SovosDatePicker;